import { FC, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch as useDispatchEffect } from "react-redux";
import { useLocation } from "react-router";
import { parse } from "@/utils/queryStringWrapper"
import { raiseRouteOverwatchUsrProfile } from "@/actions/routeOverwatch";
import authProvider, { MfaLoginInput } from "@/authProvider/authProvider";
import { LoginResponseDto, MfaWay } from "@/gen/api/user";
import { push } from "connected-react-router";
import { retrieveDeviceFlowInfo } from "./Device.common";
import { OnFailure, OnSuccess, useAuthState, useSaveModifiers } from "ra-core";
import { LoginControllerProps, Status, useLoginContext, buildReducer } from "./LoginContext";
import { CreateProps } from "ra-core/esm/controller/details/useCreateController";
import CreateBase from "@/controller/Create";
import SaveContextProvider from "@/controller/SaveContextProvider";




export const useLoginController = (): LoginControllerProps => {
  const dispatchEffect = useDispatchEffect()
  
  const [states, dispatch] = useReducer(
    buildReducer(useMemo(() => ({dispatchEffect}), [dispatchEffect])), 
    {
      username: '',
      password: '',
      phase1Result: undefined as LoginResponseDto|undefined,
      status: Status.DetermineSignIn,
      determining: false,
      determined: false,
      ssoRedirectUrl: {url:''},
      ssoUsername: '',
      explicitLocal: false,
      errMsg: '',
    })

  const { current: bag } = useRef<any>({})
  const location = useLocation<{usercode:string}>()
  const verifyDevice = 'true' === parse(location.search)['verify-device']
  const deviceFlow = useMemo(() => {
    return verifyDevice
      ? retrieveDeviceFlowInfo()
      : undefined
  }, [verifyDevice])
  
  const { loaded, authenticated } = useAuthState()
  useEffect(() => {
    if (!verifyDevice && loaded && authenticated) {
      dispatchEffect(raiseRouteOverwatchUsrProfile())
    }
  }, [loaded, authenticated, verifyDevice])

  const Create: FC<CreateProps> = useMemo(() => {
    const LoginConfirmCreate : FC<CreateProps> = ({
      children, ...rest
    }) => {
      const [saving, setSaving] = useState(false)
      const modifiers = useSaveModifiers(rest)
      const { states, deviceFlow } = useLoginContext()

      const save = (code: string, redirectTo:string, callbacks?: {
        onSuccess?: OnSuccess;
        onFailure?: OnFailure;
      }) => {
        setSaving(true)
        const info: MfaLoginInput = {
          type: 'MfaLogin',
          username: states.username,
          password: states.password,
          loginId: states.phase1Result?.id ?? 0,
          vCode: code,
          vCodeId: states.phase1Result?.vCodeId ?? 0,
          mfaWay: states.phase1Result?.mfaWay ?? MfaWay.Email,
          deviceFlow,
        }
        return authProvider.login(info).then(() => {
          if (localStorage.getItem('remember_me') === 'true') {
            localStorage.setItem('init_email', states.username)
          }
          setSaving(false)
          if (deviceFlow) {
            dispatchEffect(push('/device-consent'))
          } else {
            dispatchEffect(raiseRouteOverwatchUsrProfile())
          }
          if (callbacks?.onSuccess) {
            callbacks.onSuccess()
          }
        }, (err:any) => {
          setSaving(false)
          if (callbacks?.onFailure) {
            callbacks.onFailure(err)
          }
        })
      }
      return (
        <SaveContextProvider value={{ save, saving, ...modifiers }}>
          {children}
        </SaveContextProvider>
      )
    }

    const Create: FC<CreateProps> = ({
      resource, children, ...rest
    }) => {
      return resource !== 'login-confirm'
        ? (<CreateBase resource={resource} {...rest} >{children}</CreateBase>)
        : (<LoginConfirmCreate {...rest} >{children}</LoginConfirmCreate>)
    }
    return Create
  }, [])
  
  return {
    deviceFlow,
    states,
    bag,
    dispatch,
    Create,
  }
}
